<template>
    <div class="flex flex-col w-full">
<!--        <span class="text-xs text-gray-700">-->
<!--            {{ field.translated?.label }}-->
<!--            <span v-if="isRequired" class="text-[rgba(10,69,143,1)]">*</span>-->
<!--        </span>-->

        <div class="relative w-full">
            <button
                type="button"
                @click="toggleDropdown"
                class="flex w-full min-h-11 items-start justify-between gap-2 border border-gray-300 rounded-[12px] pl-3 pr-3 py-2.5 focus:ring-2 focus:ring-hu-green focus:border-hu-green text-sm font-bold text-gray-700 transition-all"
                :class="hasError ? 'ring-1 ring-red-500' : 'border-gray-300'"
            >
                <div class="flex min-w-0 flex-1 items-start gap-2">
                    <!-- Fixed 20×20 slot so flag load / auto-select never shifts layout -->
                    <span
                        class="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full bg-transparent"
                        aria-hidden="true"
                    >
                        <img
                            v-if="selectedOptionFlag"
                            :src="selectedOptionFlag"
                            :alt="selectedOptionAlt"
                            width="20"
                            height="20"
                            decoding="async"
                            class="h-5 w-5 max-h-5 max-w-5 rounded-full object-cover"
                        />
                    </span>
                    <span class="min-w-0 flex-1 whitespace-normal text-left leading-snug">
                        {{ selectedOptionLabel }}
                    </span>
                </div>

                <span class="mt-1 inline-flex h-4 w-4 shrink-0 items-center justify-center overflow-hidden" aria-hidden="true">
                    <img
                        v-if="resolvedAngleDownSolid"
                        :src="resolvedAngleDownSolid"
                        :alt="angleDownSolidAlt"
                        width="16"
                        height="16"
                        decoding="async"
                        class="h-4 w-4 max-h-4 max-w-4 object-contain"
                    />
                </span>
            </button>


            <div
                v-if="isDropdownOpen"
                class="absolute z-20 mt-1 flex w-full max-h-60 flex-col overflow-hidden rounded-[12px] border bg-white shadow"
            >
                <div class="shrink-0 border-b border-gray-100 bg-white p-2">
                    <input
                        type="text"
                        v-model="searchQuery"
                        placeholder="Search..."
                        class="w-full rounded border px-3 py-2 text-sm"
                    />
                </div>

                <div class="min-h-0 flex-1 overflow-y-auto">
                    <div
                        v-for="(option, index) in filteredOptions"
                        :key="option.code"
                        class="flex cursor-pointer items-start px-4 py-2.5 text-sm hover:bg-gray-100"
                        @click="selectOption(option)"
                        :class="{'bg-gray-200': option.code === currentValue}"
                    >
                        <span class="mr-2 mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full">
                            <img
                                v-if="option.flag"
                                :src="option.flag"
                                :alt="optionAlt(option)"
                                width="20"
                                height="20"
                                decoding="async"
                                class="h-5 w-5 max-h-5 max-w-5 rounded-full object-cover"
                            />
                        </span>
                        <span class="min-w-0 flex-1 whitespace-normal leading-snug">{{ option.translated?.name }}</span>
                    </div>
                </div>
            </div>
        </div>
        <!--            <p v-if="hasError" class="text-xs text-red-500 mt-1">This field is required</p>-->
    </div>
</template>

<script>
import { resolveDefaultVehicleCountryCode } from '../../../utils/languageDefaultCountry.js';

export default {
    name: "DropdownField",
    emits: ['clear-error'],
    props: {
        field: {
            type: Object,
            required: true
        },
        stepData: {
            type: Object,
            required: true
        },
        hasError: { type: Boolean, default: false },
        angleDownSolid: {
            type: [String, Object],
            required: false,
            default: null
        }
    },
    data() {
        return {
            isDropdownOpen: false,
            searchQuery: ''
        };
    },
    computed: {
        resolvedAngleDownSolid() {
            if (!this.angleDownSolid) return null;
            if (typeof this.angleDownSolid === 'string') return this.angleDownSolid;
            return this.angleDownSolid.src ?? this.angleDownSolid.url ?? null;
        },
        angleDownSolidAlt() {
            if (!this.angleDownSolid || typeof this.angleDownSolid !== 'object') return '';
            return this.angleDownSolid.translated?.alt ?? '';
        },
        isRequired() {
            return this.field.validation_rules?.includes("required");
        },
        /** Flat field key or nested e.g. invoice_vat_number + nestedKey "country" */
        currentValue() {
            if (this.field.nestedKey) {
                const parent = this.stepData[this.field.key];
                if (!parent || typeof parent !== 'object') return '';
                return parent[this.field.nestedKey] || '';
            }
            return this.stepData[this.field.key] || '';
        },
        selectedOptionLabel() {
            const value = this.currentValue;
            if (!value) {
                return this.$store?.getters?.texts?.['select-option'] ?? 'Select option';
            }

            const option = this.field.options.find(o => o.code === value);
            return option ? option.translated?.name : "Unknown";
        },
        selectedOptionFlag() {
            const value = this.currentValue;
            if (!value) {
                return '';
            }

            const option = this.field.options.find(o => o.code === value);
            return option ? option.flag : '';
        },
        selectedOptionAlt() {
            const value = this.currentValue;
            if (!value) return '';
            const option = this.field.options.find(o => o.code === value);
            return option ? this.optionAlt(option) : '';
        },
        filteredOptions() {
            if (!this.searchQuery) return this.field.options;

            const query = this.searchQuery.toLowerCase();
            return this.field.options.filter(option =>
                (option.translated?.name || '').toLowerCase().includes(query) ||
                (option.code || '').toLowerCase().includes(query)
            );
        }
    },
    created() {
        // Before first paint — avoids Select option → United Kingdom layout shift (CLS).
        this.applyDefaultOption();
    },
    watch: {
        'field.options': {
            handler() {
                this.applyDefaultOption();
            },
            deep: false
        }
    },
    mounted() {
        document.addEventListener('click', this.handleOutsideClick);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleOutsideClick);
    },
    methods: {
        applyDefaultOption() {
            if (this.field.preventAutoSelect || this.field.nestedKey) {
                return;
            }
            if (this.stepData[this.field.key] || !this.field.options?.length) {
                return;
            }

            let code;
            if (
                (this.field.key === 'plate_country' || this.field.key === 'invoice_country')
                && this.$store
            ) {
                const lang = this.$store.getters.lang;
                code = resolveDefaultVehicleCountryCode(lang?.code, this.field.options);
            } else {
                code = this.field.options[0]?.code;
            }
            if (code) this.stepData[this.field.key] = code;
        },
        optionAlt(option) {
            return option?.translated?.name || option?.code || '';
        },
        handleOutsideClick(event) {
            if (!this.$el.contains(event.target)) {
                this.isDropdownOpen = false;
            }
        },
        toggleDropdown() {
            this.isDropdownOpen = !this.isDropdownOpen;
            if (this.isDropdownOpen) {
                this.searchQuery = '';
            }
        },
        selectOption(option) {
            if (this.field.nestedKey) {
                if (!this.stepData[this.field.key] || typeof this.stepData[this.field.key] !== 'object') {
                    this.stepData[this.field.key] = { country: '', number: '' };
                }
                this.stepData[this.field.key][this.field.nestedKey] = option.code;
            } else {
                this.stepData[this.field.key] = option.code;
            }
            this.isDropdownOpen = false;
            const errKey = this.field.nestedKey
                ? `${this.field.key}.${this.field.nestedKey}`
                : this.field.key;
            this.$emit('clear-error', errKey);
        }
    }
};
</script>
